<template>
  <main :class="['main-content', { 'with-side-bar': showSideBar }]">
    <div class="table-wrapper d-flex justify-content-center">
      <div class="table">
        <div class="boon-row header">
          <div class="cell">{{ $t('Company name') }}</div>
          <div class="cell cell-short-text">{{ $t('Phone') }}</div>
          <div class="cell">{{ $t('Email') }}</div>
          <div class="cell cell-address">{{ $t('Address') }}</div>
          <div class="cell cell-short-text">{{ $t('Type') }}</div>
        </div>

        <virtual-list
          data-key="id"
          class="scroller"
          :data-sources="items"
          :data-component="itemComponent"
          :extra-props="{ onToggleExpanded, onSelectionUpdate, selection }"
          :estimate-size="47"
          item-class="group"
        />
      </div>
    </div>
  </main>
</template>

<script>
import VirtualList from 'vue-virtual-scroll-list';
import { mapGetters } from 'vuex';

import { extractMentions } from '@/common/filters';
import CompanyListRow from './CompanyListRow';

export default {
  name: 'PersonList',

  components: {
    CompanyListRow,
    VirtualList,
  },

  props: {
    filterText: {
      type: String,
    },
    filterCity: {
      type: String,
    },
    filterOccupation: {
      type: String,
    },
    withMissingUeFile: {
      type: Boolean,
      default: false,
    },
    withCodeOfConductStatus: {
      type: String,
      default: null,
    },
    showSideBar: {
      type: Boolean,
      default: false,
    },
    selection: {
      type: Array,
      required: true,
    },
  },

  data() {
    return {
      items: [],
      itemComponent: CompanyListRow,
    };
  },

  computed: {
    ...mapGetters(['companiesByFilter', 'persons']),

    filter() {
      const { text, mentions } = extractMentions(this.filterText);
      return {
        text,
        city: this.filterCity,
        occupation: this.filterOccupation,
        withMissingUeFile: this.withMissingUeFile,
        withCodeOfConductStatus: this.withCodeOfConductStatus,
        projectId: mentions.length > 0 ? mentions[0] : null,
      };
    },

    hasFilters() {
      return Object.values(this.filter).some((value) => !!value);
    },

    filteredCompanies() {
      return this.companiesByFilter(this.filter);
    },

    filteredPersons() {
      return this.persons(this.filter);
    },

    allPersons() {
      return this.persons();
    },
  },

  methods: {
    onToggleExpanded({ id, value }) {
      const item = this.items.find((item) => item.id === id);
      if (item) {
        item.expanded = value;
      }
    },

    onSelectionUpdate(company, selectedPersons) {
      let selection = [...this.selection];
      if (selectedPersons.length) {
        const index = selection.findIndex(({ companyId }) => companyId === company._id);
        selection.splice(index, index >= 0 ? 1 : 0, {
          companyId: company._id,
          items: [...selectedPersons],
        });
      } else {
        selection = selection.filter(({ companyId }) => companyId !== company._id);
      }

      this.$emit('update:selection', selection);
    },
  },

  watch: {
    filteredCompanies: {
      immediate: true,
      handler(nextValue) {
        const prevExpanded = {};
        if (this.items) {
          this.items.forEach(({ id, expanded }) => {
            prevExpanded[id] = expanded;
          });
        }

        this.items = nextValue.items.map((company) => {
          // If we have a hit on persons, show only found persons. Otherwise show all, because we
          // have previously found a hit on company.
          let persons = this.filteredPersons[company._id];
          if (!persons || !persons.length || !nextValue.matchByPersons.includes(company._id)) {
            persons = this.allPersons[company._id];
          }
          return {
            id: company._id,
            company,
            persons: persons || [],
            expanded: prevExpanded[company._id],
            hasFilter: this.filter.text.length > 1,
          };
        });

        const noCompanyId = '';
        const unassignedPersons = this.filteredPersons[noCompanyId];
        if (unassignedPersons) {
          this.items.push({
            id: noCompanyId,
            company: null,
            persons: unassignedPersons,
            expanded: prevExpanded[noCompanyId],
            hasFilter: this.filter.text.length > 1,
          });
        }
      },
    },

    items(nextValue) {
      this.$emit('update:items', nextValue);
    },

    filter(nextValue, prevValue) {
      // Reset selection when filter changes.
      if (Object.keys(nextValue).some((key) => nextValue[key] !== prevValue[key])) {
        this.$emit('update:selection', []);
      }
    },
  },
};
</script>

<style scoped>
.main-content {
  display: flex;
  flex-direction: row;
  justify-content: space-around;
  /* Header's height is 80px; subheader's height is 3.375rem */
  max-height: calc(100vh - 80px - 3.375rem);
  overflow-y: hidden;
}

@media (max-width: 768px) {
  .main-content {
    max-height: 100vh;
  }
}

.with-side-bar {
  margin-left: 250px;
}

.table-wrapper {
  max-width: 1920px;
  width: 100%;
}

.table {
  display: flex;
  flex-flow: column;
  background-color: #fff;
  color: #000;
  max-width: 1344px;
  max-height: 100%;
  margin-bottom: 0;
}

.scroller {
  height: 100%;
  overflow-y: auto;
}

/* Overriding Bootstrap's class to remove border between rows for this table */
.main-content >>> .table > :not(:first-child) {
  border-top: none;
}

.boon-row {
  display: flex;
  flex-flow: row wrap;
}

.header {
  background: #3a4652;
  color: #fff;
  padding: 10px;
  font-size: 0.8125rem;
}

.header .cell {
  width: 300px;
}

.main-content >>> .cell-address {
  flex-grow: 1;
}

.header .cell:not(:last-child) {
  padding-left: 3.5em;
}

.header .cell:last-child {
  padding-left: 1.5em;
}

.main-content >>> .cell-short-text {
  max-width: 190px;
  min-width: 190px;
}

.main-content >>> .cell:not(.narrow) {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.main-content >>> .scroller .group {
  padding: 0;
}

.main-content >>> .group > .boon-row {
  background: #f6f6f6;
  padding: 0;
}

.main-content >>> .group:nth-of-type(even) > .boon-row {
  background: #fff;
}

a:visited {
  color: inherit;
}
</style>



// WEBPACK FOOTER //
// src/components/organisation/PersonList.vue